import React, { useState, useEffect, useRef } from 'react';
import { getRankedProviders } from '../lib/triage/routeEngine';
import { getResultContent } from '../lib/triage/explanations';
import { saveTriageSession, logProviderImpressions, logProviderClick } from '../lib/triage/triageService';
import { getState } from '../lib/userStore';

// ─── Care level metadata ──────────────────────────────────────────────────────

const CARE_LEVEL_STYLES = {
  emergency_now: {
    bg: '#fdf2f0',
    accent: '#c0392b',
    label: 'Call 911 now',
    subtext: 'This needs emergency care immediately.',
    sectionLabel: 'Nearest emergency care',
  },
  care_today: {
    bg: 'var(--amber-light)',
    accent: 'var(--amber)',
    label: 'Get care today',
    subtext: 'See a provider within the next few hours.',
    sectionLabel: 'Best options near you',
  },
  care_soon: {
    bg: 'var(--teal-faint)',
    accent: 'var(--teal)',
    label: 'See someone soon',
    subtext: 'Make an appointment in the next 1–2 days.',
    sectionLabel: 'Recommended providers',
  },
  home_care: {
    bg: 'var(--cream-2)',
    accent: 'var(--ink-3)',
    label: 'Manage at home',
    subtext: 'Rest, fluids, and monitor. Here\'s what to watch for.',
    sectionLabel: 'If you do want to be seen',
  },
};

// ─── Insurance label ──────────────────────────────────────────────────────────

const CARRIER_NAMES = {
  bcbs: 'BCBS',
  aetna: 'Aetna',
  cigna: 'Cigna',
  united: 'UnitedHealthcare',
  humana: 'Humana',
  medicaid: 'Medicaid',
  medicare: 'Medicare',
};
const PLAN_NAMES = { ppo: 'PPO', hmo: 'HMO', hdhp: 'HDHP', epo: 'EPO' };

function insuranceLabel(carrier, planType) {
  const c = carrier ? (CARRIER_NAMES[carrier] || carrier) : null;
  const p = planType ? (PLAN_NAMES[planType] || planType) : null;
  if (c && p) return `${c} ${p}`;
  return c || null;
}

// ─── Provider card ────────────────────────────────────────────────────────────

function ProviderCard({ provider, careLevel, userInsuranceLabel, isEmergency, onActionClick }) {
  const [expanded, setExpanded] = useState(false);

  const insuranceMatch = (() => {
    if (!userInsuranceLabel || !provider.insurance) return false;
    const ins = typeof provider.insurance === 'string'
      ? provider.insurance.toLowerCase()
      : JSON.stringify(provider.insurance).toLowerCase();
    return ins.includes(userInsuranceLabel.split(' ')[0].toLowerCase());
  })();

  // Nurse line — stored on the provider or falls back to maps URL
  const nurseLinePhone = provider.phone || null;

  return (
    <div style={s.card}>
      {provider.featured && (
        <div style={s.featuredBar}>Featured provider</div>
      )}
      <button style={s.cardBody} onClick={() => setExpanded(e => !e)}>
        <div style={s.cardTop}>
          <div style={s.cardName}>{provider.name}</div>
          {provider.status === 'accepting' && (
            <span style={s.acceptingBadge}>Accepting</span>
          )}
        </div>
        <div style={s.cardMeta}>
          {provider.specialty || provider.category}
          {provider.same_day && ' · Same-day'}
          {provider.telehealth && ' · Telehealth'}
        </div>
        {insuranceMatch && userInsuranceLabel && (
          <div style={s.insuranceMatch}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="var(--teal)" />
              <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Takes your {userInsuranceLabel}</span>
          </div>
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div style={s.expandedDetail}>
          {provider.description && (
            <p style={s.expandedText}>{provider.description}</p>
          )}
          {provider.hours && (
            <p style={{ ...s.expandedText, color: 'var(--ink-3)' }}>{provider.hours}</p>
          )}
          {provider.insurance_note && (
            <p style={{ ...s.expandedText, fontStyle: 'italic' }}>{provider.insurance_note}</p>
          )}
        </div>
      )}

      {/* Action row */}
      <div style={s.actionRow}>
        {isEmergency && nurseLinePhone && (
          <a
            href={`tel:${nurseLinePhone.replace(/\D/g, '')}`}
            style={s.nurseLineBtn}
          >
            📞 Call nurse line · {nurseLinePhone}
          </a>
        )}
        {!isEmergency && (
          <>
            {provider.phone && (
              <a href={`tel:${provider.phone.replace(/\D/g, '')}`} style={s.actionBtn} onClick={() => onActionClick && onActionClick('call')}>
                Call
              </a>
            )}
            {provider.maps_url && (
              <a href={provider.maps_url} target="_blank" rel="noopener noreferrer" style={s.actionBtn} onClick={() => onActionClick && onActionClick('directions')}>
                Directions
              </a>
            )}
            {provider.book_url && (
              <a href={provider.book_url} target="_blank" rel="noopener noreferrer" style={s.actionBtn} onClick={() => onActionClick && onActionClick('book')}>
                Book
              </a>
            )}
          </>
        )}
        {isEmergency && provider.maps_url && (
          <a href={provider.maps_url} target="_blank" rel="noopener noreferrer" style={s.directionsBtnSecondary}>
            Get directions
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Skeleton placeholder ─────────────────────────────────────────────────────

function ProviderSkeleton() {
  return (
    <div style={{ ...s.card, opacity: 0.5 }}>
      <div style={{ ...s.skeletonLine, width: '60%', marginBottom: 8 }} />
      <div style={{ ...s.skeletonLine, width: '40%', marginBottom: 12 }} />
      <div style={{ ...s.skeletonLine, width: '80%' }} />
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export default function GetCareResultScreen({ navigate, goBack, user, triageResult, answers, protocolId, ageBand, personType }) {
  const { careLevel, reasonCodes } = triageResult;
  const { explanation, selfCare, safetyNet } = getResultContent(protocolId, careLevel, reasonCodes, answers);
  const levelStyle = CARE_LEVEL_STYLES[careLevel] || CARE_LEVEL_STYLES.care_soon;
  const isEmergency = careLevel === 'emergency_now';

  const store = getState();
  const insLabel = insuranceLabel(store.carrier, store.planType);

  const [providers, setProviders] = useState(null);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const triageSessionIdRef = useRef(null);

  // Save session then load providers
  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Persist session first (anon or signed-in)
      const sessionId = await saveTriageSession({
        userId:     user?.id || null,
        protocolId,
        personType: personType || 'self',
        ageBand,
        answers,
        triageResult,
      });
      if (!cancelled) triageSessionIdRef.current = sessionId;

      // Load ranked providers
      try {
        const results = await getRankedProviders(
          triageResult,
          { ageBand, userLat: store.userLat, userLng: store.userLng }
        );
        if (!cancelled) {
          setProviders(results);
          setProvidersLoaded(true);
          if (sessionId && results.length > 0) {
            logProviderImpressions(sessionId, user?.id || null, results);
          }
        }
      } catch {
        if (!cancelled) {
          setProviders([]);
          setProvidersLoaded(true);
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleProviderClick(provider, action) {
    const sid = triageSessionIdRef.current;
    if (sid && provider.npi) logProviderClick(sid, provider.npi, action);
  }

  return (
    <div style={s.shell}>
      {/* Header */}
      <div style={s.header}>
        <button onClick={goBack} style={s.backBtn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 5l-7 7 7 7" stroke="var(--ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Your result</span>
        <div style={{ minWidth: 32 }} />
      </div>

      {/* Body */}
      <div style={s.body}>
        <div style={s.bodyInner}>

          {/* ── Care level card ────────────────────────────────────────────── */}
          <div style={{ ...s.careLevelCard, background: levelStyle.bg }}>
            <div style={{ ...s.careLevelLabel, color: levelStyle.accent }}>
              {levelStyle.label}
            </div>
            <p style={{ ...s.careLevelSubtext, color: levelStyle.accent }}>
              {levelStyle.subtext}
            </p>

            {/* Personalized explanation */}
            {explanation && (
              <div style={s.explanationWrap}>
                <p style={{ ...s.explanationText, color: levelStyle.accent }}>
                  {explanation}
                </p>
              </div>
            )}

            {/* Emergency: 911 call button */}
            {isEmergency && (
              <a href="tel:911" style={s.call911Btn}>
                Call 911
              </a>
            )}
          </div>

          {/* ── Providers section ──────────────────────────────────────────── */}
          {(!isEmergency || (providers && providers.length > 0)) && (
            <div>
              <p style={s.sectionLabel}>{levelStyle.sectionLabel}</p>

              {!providersLoaded ? (
                <>
                  <ProviderSkeleton />
                  <ProviderSkeleton />
                  <ProviderSkeleton />
                </>
              ) : providers && providers.length > 0 ? (
                <>
                  {providers.map((provider, i) => (
                    <div
                      key={provider.npi || i}
                      style={{
                        animationDelay: `${i * 60}ms`,
                        animation: 'fadeSlideUp 0.3s ease-out both',
                      }}
                    >
                      <ProviderCard
                        provider={provider}
                        careLevel={careLevel}
                        userInsuranceLabel={insLabel}
                        isEmergency={isEmergency}
                        onActionClick={(action) => handleProviderClick(provider, action)}
                      />
                    </div>
                  ))}

                  {/* See all link — non-emergency only */}
                  {!isEmergency && (
                    <button
                      style={s.seeAllLink}
                      onClick={() => navigate('findCare')}
                    >
                      See all providers →
                    </button>
                  )}
                </>
              ) : (
                /* No providers found */
                <div style={s.emptyState}>
                  <p style={s.emptyText}>
                    We're still building our Greenville directory for this category.
                  </p>
                  <button style={s.emptyBtn} onClick={() => navigate('findCare')}>
                    Browse all providers
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Self-care tips — home_care and care_soon */}
          {selfCare && selfCare.length > 0 && (
            <div>
              <p style={s.sectionLabel}>What to do at home</p>
              <div style={s.selfCareList}>
                {selfCare.map((tip, i) => (
                  <div key={i} style={s.selfCareItem}>
                    <span style={s.selfCareNum}>{i + 1}</span>
                    <span style={s.selfCareTip}>{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Auth nudge (non-emergency, anonymous users) */}
          {!isEmergency && !user && (
            <div style={s.authNudge}>
              <p style={s.authNudgeText}>
                Save this result and get a 48-hour check-in reminder.
              </p>
              <button
                style={s.authNudgeBtn}
                onClick={() => navigate('auth')}
              >
                Sign in or create account
              </button>
            </div>
          )}

          {/* Safety-net note */}
          {safetyNet && (
            <p style={s.safetyNet}>{safetyNet}</p>
          )}

        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    background: 'var(--cream)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--surface)',
    flexShrink: 0,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    minWidth: 32,
  },
  headerTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '17px',
    fontWeight: 700,
    color: 'var(--ink)',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  bodyInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: '20px 16px 48px',
  },
  careLevelCard: {
    borderRadius: 'var(--radius-lg)',
    padding: '24px 20px',
  },
  careLevelLabel: {
    fontFamily: 'var(--font-display)',
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: 8,
    lineHeight: 1.1,
  },
  careLevelSubtext: {
    fontFamily: 'var(--font-body)',
    fontSize: '16px',
    margin: '0 0 16px',
    lineHeight: 1.5,
    opacity: 0.85,
  },
  call911Btn: {
    display: 'block',
    textAlign: 'center',
    padding: '16px',
    background: '#c0392b',
    color: '#fff',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)',
    fontSize: '18px',
    fontWeight: 700,
    textDecoration: 'none',
    letterSpacing: '0.02em',
  },
  sectionLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--ink-4)',
    margin: '0 0 12px',
  },
  card: {
    background: 'var(--surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
    marginBottom: 12,
  },
  featuredBar: {
    background: 'var(--teal)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '5px 18px',
  },
  cardBody: {
    display: 'block',
    width: '100%',
    padding: '16px 18px 12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    WebkitTapHighlightColor: 'transparent',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
  },
  cardName: {
    fontFamily: 'var(--font-body)',
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--ink)',
    lineHeight: 1.3,
    flex: 1,
  },
  acceptingBadge: {
    background: '#e8f5e9',
    color: '#2e7d32',
    fontFamily: 'var(--font-body)',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    padding: '3px 8px',
    borderRadius: 20,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  cardMeta: {
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--ink-3)',
    marginBottom: 8,
  },
  insuranceMatch: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--teal)',
    fontWeight: 600,
  },
  expandedDetail: {
    padding: '0 18px 12px',
  },
  expandedText: {
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--ink-2)',
    lineHeight: 1.5,
    margin: '0 0 6px',
  },
  actionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '0 18px 16px',
  },
  actionBtn: {
    flex: 1,
    textAlign: 'center',
    padding: '10px',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--teal)',
    background: 'var(--teal-faint)',
    textDecoration: 'none',
    display: 'inline-block',
  },
  nurseLineBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '14px 20px',
    borderRadius: 'var(--radius-md)',
    border: '2px solid #c0392b',
    background: 'white',
    color: '#c0392b',
    fontSize: '15px',
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    textDecoration: 'none',
    boxSizing: 'border-box',
  },
  directionsBtnSecondary: {
    display: 'block',
    textAlign: 'center',
    padding: '10px',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--ink-2)',
    background: 'var(--cream-2)',
    textDecoration: 'none',
  },
  skeletonLine: {
    height: 14,
    borderRadius: 7,
    background: 'var(--cream-2)',
    margin: '8px 18px',
  },
  seeAllLink: {
    display: 'block',
    width: '100%',
    textAlign: 'right',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    color: 'var(--teal)',
    padding: '4px 0',
    marginTop: 4,
  },
  emptyState: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: '20px',
    textAlign: 'center',
  },
  emptyText: {
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    color: 'var(--ink-3)',
    margin: '0 0 16px',
    lineHeight: 1.5,
  },
  emptyBtn: {
    background: 'var(--teal)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    padding: '12px 20px',
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  authNudge: {
    background: 'var(--cream-2)',
    borderRadius: 'var(--radius-md)',
    padding: '16px',
    textAlign: 'center',
  },
  authNudgeText: {
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    color: 'var(--ink-2)',
    margin: '0 0 12px',
    lineHeight: 1.4,
  },
  authNudgeBtn: {
    background: 'none',
    border: '1.5px solid var(--teal)',
    color: 'var(--teal)',
    borderRadius: 'var(--radius-sm)',
    padding: '10px 20px',
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  explanationWrap: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid rgba(255,255,255,0.30)',
  },
  explanationText: {
    fontFamily: 'var(--font-display)',
    fontSize: '15px',
    fontStyle: 'italic',
    lineHeight: 1.55,
    margin: 0,
    opacity: 0.92,
  },
  selfCareList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  selfCareItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  selfCareNum: {
    flexShrink: 0,
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: 'var(--teal-faint)',
    color: 'var(--teal)',
    fontFamily: 'var(--font-body)',
    fontSize: '11px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  selfCareTip: {
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    color: 'var(--ink-2)',
    lineHeight: 1.55,
  },
  safetyNet: {
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    fontStyle: 'italic',
    color: 'var(--ink-4)',
    lineHeight: 1.5,
    textAlign: 'center',
    margin: 0,
    padding: '0 8px',
  },
};
